import React from "react";
import { ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";

interface ExpressCheckoutButtonProps {
  clientSecret: string;
  onPaymentSuccess: (paymentMethodId: string) => Promise<void>;
  onError: (message: string) => void;
  onProcessing: (message: string) => void;
  onReady?: (available: boolean) => void;
}

const ExpressCheckoutButton: React.FC<ExpressCheckoutButtonProps> = ({
  clientSecret,
  onPaymentSuccess,
  onError,
  onProcessing,
  onReady,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleReady = (event: { availablePaymentMethods?: any }) => {
    // Check if any payment methods are available
    const available = event.availablePaymentMethods && 
                      Object.keys(event.availablePaymentMethods).length > 0;
    onReady?.(available);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      return;
    }

    onProcessing("Processing your payment...");

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onError(submitError.message || "Payment failed");
        return;
      }

      // Confirm the payment
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (error) {
        onError(error.message || "Payment failed");
      } else if (paymentIntent?.status === "succeeded") {
        onProcessing("Setting up payment plan...");
        await onPaymentSuccess(paymentIntent.payment_method as string);
      }
    } catch (error) {
      if (error instanceof Error) {
        onError(error.message);
      } else {
        onError("An unknown error occurred");
      }
    }
  };

  return (
    <div className="bg-gray-800 rounded p-4">
      <ExpressCheckoutElement
        onConfirm={handleConfirm}
        onReady={handleReady}
        options={{
          buttonType: {
            applePay: "buy",
            googlePay: "buy",
          },
          wallets: {
            applePay: "auto",
            googlePay: "auto",
          },
        }}
      />
    </div>
  );
};

export default ExpressCheckoutButton;
